import {
  Order,
  OrderState,
  RobotDelivery,
  RobotProofOfDelivery,
  StateChange,
} from '../types/Order'

// Nabd robot indoor leg (epic 29229, sc-29236).
//
// The tracking page flips into "robot mode" purely on the order carrying
// `robotDelivery` (written by ex-core-api's Nabd webhook): Nabd's floor-plan
// widget replaces the courier map, a robot card replaces the courier card, and
// Leave-at-door / Update-address disappear. Everything the widget tells us
// arrives as `postMessage` from the iframe (spec §10.3) — the page never polls.

/** `type` of the messages the widget posts to its parent (spec §10.3). */
export const NABD_STATUS_MESSAGE_TYPE = 'nabd.delivery.status'

/** Synthetic stepper state for the "With robot" step; never a real order state. */
export const ROBOT_LOADED_STATE = 'robot_loaded'

export type NabdPhase =
  | 'AT_ENTRANCE'
  | 'IN_LIFT'
  | 'ON_FLOOR'
  | 'APPROACHING'
  | 'AT_PARTNER_STORE'

export type NabdStatus =
  | 'ACCEPTED'
  | 'LOADED'
  | 'IN_TRANSIT'
  | 'AT_DESTINATION'
  | 'DELIVERED'
  | 'FAILED'
  | 'ON_HOLD'
  | 'CANCELLED'
  | 'RETURNED'

/** What the robot card shows: the latest widget message, or a seed from the payload. */
export interface RobotStatus {
  status?: NabdStatus | string
  phase?: NabdPhase | string | null
  floor?: number | null
  etaMinutes?: number | null
}

export interface NabdStatusMessage extends RobotStatus {
  type: typeof NABD_STATUS_MESSAGE_TYPE
}

// Once the order is terminal Nabd's terminal event has closed it; the widget
// keeps resolving for 24h but there is nothing left to watch.
const TERMINAL_ORDER_STATES: OrderState[] = [
  'delivery_complete',
  'delivery_failed',
  'cancelled',
  'return_to_origin',
  'returned_to_origin',
]

// Widget statuses after which the order itself has moved (spec §10.3). The page
// re-fetches the order on these so the stepper advances from real state changes.
const TERMINAL_WIDGET_STATUSES = [
  'DELIVERED',
  'FAILED',
  'CANCELLED',
  'RETURNED',
]

// Pixels of the widget our own cards cover: the status card over the top, the
// robot card over the bottom. Measured by Nabd on our page (spec §10.2); the
// robot and its text are kept inside the strip that stays visible.
const WIDGET_INSET = '80,90'

export function hasRobotLeg(order?: Order | null): boolean {
  return Boolean(order?.robotDelivery?.widgetUrl)
}

/** Whether to render the widget: a robot leg that is neither over nor expired. */
export function hasActiveRobotWidget(
  order?: Order | null,
  now: Date = new Date()
): boolean {
  if (!order || !hasRobotLeg(order)) {
    return false
  }
  if (TERMINAL_ORDER_STATES.includes(order.state)) {
    return false
  }
  const expiresAt = order.robotDelivery?.widgetExpiresAt
  if (expiresAt && new Date(expiresAt).getTime() <= now.getTime()) {
    return false
  }
  // A URL we cannot derive an origin from cannot be framed safely (the
  // message listener keys on that origin), so fall back to the courier map
  // rather than reserving an empty slot.
  return widgetOrigin(order.robotDelivery!.widgetUrl) !== null
}

/**
 * The iframe `src`: Nabd's per-order URL (token included) plus our parameters.
 * `lang` is pinned to `en` because this app has no i18n; `brand` is omitted on
 * purpose — the widget defaults to the order's partner palette (spec §10.1).
 */
export function buildWidgetSrc(widgetUrl: string): string {
  const url = new URL(widgetUrl)
  url.searchParams.set('inset', WIDGET_INSET)
  url.searchParams.set('lang', 'en')
  return url.toString()
}

/**
 * The only origin whose messages we accept. Derived from the URL rather than
 * hardcoded: the spec names just the demo host and production's is unknown.
 */
export function widgetOrigin(widgetUrl: string): string | null {
  try {
    return new URL(widgetUrl).origin
  } catch {
    return null
  }
}

export function isNabdStatusMessage(data: unknown): data is NabdStatusMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { type?: unknown }).type === NABD_STATUS_MESSAGE_TYPE
  )
}

export function isTerminalWidgetStatus(status?: string): boolean {
  return Boolean(status && TERMINAL_WIDGET_STATUSES.includes(status))
}

// Deliberately no robot identifiers here (id, model, operator): the card tells
// the customer where their parcel is, not which machine has it.
const PHASE_TEXT: Record<string, string> = {
  AT_ENTRANCE: 'Waiting at the building entrance',
  IN_LIFT: 'In the lift',
  ON_FLOOR: 'On your floor',
  APPROACHING: 'Almost at your door',
  AT_PARTNER_STORE: "Held at the building's collection point",
  // Not a widget phase (spec §10.3) — seeded from the payload's `handed_over`
  // robot_state for the moment between the handover and the terminal refresh,
  // when the generic line would claim the parcel is still on its way.
  HANDED_OVER: 'Handed over to you',
}

const DEFAULT_STEP_TEXT = 'A robot is bringing your parcel to your floor'

// The payload's `robot_state` (spec §5.1) seeds the card before the widget's
// first message; the widget's `phase` (spec §10.3) takes over from then on.
const ROBOT_STATE_PHASE: Record<string, NabdPhase | 'HANDED_OVER'> = {
  loaded: 'AT_ENTRANCE',
  in_lift: 'IN_LIFT',
  on_floor: 'ON_FLOOR',
  at_destination: 'APPROACHING',
  held_at_partner: 'AT_PARTNER_STORE',
  handed_over: 'HANDED_OVER',
  // `recovering` deliberately has no entry: the generic line is the honest one.
}

export function initialRobotStatus(
  robotDelivery?: RobotDelivery | null
): RobotStatus | undefined {
  if (!robotDelivery) {
    return undefined
  }
  return {
    phase: robotDelivery.robotState
      ? ROBOT_STATE_PHASE[robotDelivery.robotState] ?? null
      : null,
    floor: robotDelivery.floor ?? robotDelivery.location?.floor ?? null,
    etaMinutes: robotDelivery.etaMinutes ?? null,
  }
}

export function robotStepText(status?: RobotStatus): string {
  const phase = status?.phase
  return (phase && PHASE_TEXT[phase]) || DEFAULT_STEP_TEXT
}

/**
 * Inserts the synthetic "With robot" step into the stepper's list (newest
 * first), placed by time. Only for the stepper: the header status, `hasMap`
 * and `canLeaveAtDoor` all read `uniqueStateChanges[0]` and must keep seeing
 * the real order state.
 */
export function withRobotStep(
  uniqueStateChanges: (StateChange | undefined)[],
  robotDelivery?: RobotDelivery | null
): (StateChange | undefined)[] {
  const loadedAt = robotDelivery?.loadedAt
  if (!loadedAt) {
    return uniqueStateChanges
  }
  const step: StateChange = {
    toState: ROBOT_LOADED_STATE,
    occurredAt: loadedAt,
  }
  const loadedTime = new Date(loadedAt).getTime()
  const list = [...uniqueStateChanges]
  const index = list.findIndex(
    (stateChange) =>
      stateChange && new Date(stateChange.occurredAt).getTime() <= loadedTime
  )
  if (index === -1) {
    list.push(step)
  } else {
    list.splice(index, 0, step)
  }
  return list
}

// Nabd's proof of delivery (sc-29235). For a robot order no courier is ever at
// the door, so no PIN or signature is captured anywhere — this block is the
// whole proof, and the delivered screen shows it where the courier card was.

/**
 * The proof to show, or undefined. Gated on the order actually being delivered
 * as well as on the block being present: Nabd null the block on every
 * non-terminal event, but an out-of-order arrival could leave a stale one on an
 * order that has since moved, and a proof card on an undelivered order is the
 * one thing this must never do.
 */
export function robotProof(
  order?: Order | null
): RobotProofOfDelivery | undefined {
  if (!order || order.state !== 'delivery_complete') {
    return undefined
  }
  const proof = order.robotDelivery?.proofOfDelivery
  // An all-null block (Nabd send every field on every event) is not a proof.
  return proof && Object.values(proof).some((value) => value != null)
    ? proof
    : undefined
}

const HANDOVER_METHOD_TEXT: Record<string, string> = {
  recipient_pin: 'Code entered on the robot',
  concierge: 'Handed to the building concierge',
  reception: 'Left at reception',
}

/** How the parcel changed hands, in the customer's words. */
export function handoverMethodText(method?: string | null): string {
  if (!method) {
    return 'Collected from the robot'
  }
  return HANDOVER_METHOD_TEXT[method] ?? method.replace(/_/g, ' ')
}

/**
 * Who took the parcel, or undefined when we were not told a name.
 * `recipientName` is what makes a concierge handover acceptable to us; Nabd do
 * not send it yet, so `notes` is the fallback. Both reach us only on the
 * phone-authenticated tracking path (the access code's posture), so a deep
 * link legitimately has neither and the card omits the row.
 *
 * `recipientPresent` is deliberately NOT a further fallback: it is a boolean,
 * and "true" is not a name.
 */
export function proofRecipientText(
  proof?: RobotProofOfDelivery
): string | undefined {
  return proof?.recipientName || proof?.notes || undefined
}

/**
 * The robot, for the proof only. The in-flight card deliberately names no
 * machine (agreed 2026-09-16) — the customer does not care which robot is in
 * the lift. The proof is the opposite case: sc-29235 asks for the robot id
 * precisely because it is what a dispute is argued over.
 */
export function robotIdentityText(
  robotDelivery?: RobotDelivery | null
): string | undefined {
  const carrier = robotDelivery?.carrier
  if (!carrier) {
    return undefined
  }
  return [carrier.model, carrier.id].filter(Boolean).join(' ') || undefined
}
